<div class="note" [ngClass]="{'note-editing': isEditing, 'note-pinned': note.pined}">
  <!-- Hidden file input for edit mode -->
  <input
    #editFileInput
    type="file"
    multiple
    [accept]="allowedFileTypes.join(',')"
    (change)="onFilesSelected($event)"
    style="display: none"
  />

  <div class="note-content" [ngClass]="{'note-content-editing': isEditing}">
    <!-- Pin indicator for pinned notes -->
    <div class="pin-indicator" *ngIf="note.pined && !isEditing">
      <mat-icon class="pin-icon">push_pin</mat-icon>
      <span class="pin-text">Pinned</span>
    </div>

    <div class="note-meta" *ngIf="!isEditing && !hideActions() && !focusMode()">
      <span class="note-date">{{ note.created_at | customDate }}</span>
      <span class="note-author" *ngIf="note.created_by">
        by {{ note.created_by.name }} {{ note.created_by.lastName }}
      </span>

      <span class="merged-badge" *ngIf="note.merged_from_lead_id">
        <mat-icon class="merged-icon">merge_type</mat-icon>
        Merged
      </span>
    </div>

    <!-- Editing View -->
    <div *ngIf="isEditing; else normalView" class="note-editor-container">
      <app-html-editor
        [formControl]="notesContent"
        [height]="'100px'"
        [showNoteBackground]="true"
        placeholder="Edit your note"
        [quillModule]="quillModule">
      </app-html-editor>

      <!-- Attachments in Edit Mode -->
      <div class="note-attachments editing" *ngIf="editingAttachments.length > 0 || pendingFiles.length > 0">
        <div class="attachments-label">
          <mat-icon>attach_file</mat-icon>
          <span>Attachments ({{ getTotalAttachmentCount() }}/5)</span>
        </div>
        <div class="attachments-list">
          <!-- Existing attachments -->
          <div 
            class="attachment-item editable" 
            *ngFor="let attachment of editingAttachments; let i = index">
            <mat-icon class="file-icon">{{ getFileIcon(attachment.type) }}</mat-icon>
            <span class="attachment-name" [title]="attachment.name">
              {{ attachment.name.length > 20 ? (attachment.name | slice:0:20) + '...' : attachment.name }}
            </span>
            <span class="attachment-size" *ngIf="attachment.size">
              {{ formatFileSize(attachment.size) }}
            </span>
            <button 
              class="remove-btn" 
              (click)="removeEditingAttachment(i)"
              type="button">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <!-- Pending files (new uploads) -->
          <div 
            class="attachment-item editable pending" 
            *ngFor="let file of pendingFiles; let i = index">
            <mat-icon class="file-icon new-file">{{ getFileIcon(file.type) }}</mat-icon>
            <span class="attachment-name" [title]="file.name">
              {{ file.name.length > 20 ? (file.name | slice:0:20) + '...' : file.name }}
            </span>
            <span class="attachment-size" *ngIf="file.size">
              {{ formatFileSize(file.size) }}
            </span>
            <span class="new-badge">NEW</span>
            <button 
              class="remove-btn" 
              (click)="removePendingFile(i)"
              type="button">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="note-action-buttons">
        <button mat-button class="btn-cancel" (click)="cancelEdit()">Cancel</button>
        <button
          mat-button
          (click)="triggerFileInput()"
          [disabled]="!canAddMoreFiles()">
          <mat-icon>attach_file</mat-icon>
          Attach
        </button>
        <button
          mat-raised-button
          color="primary"
          class="btn-save"
          [disabled]="!notesContent.value"
          (click)="saveEdit()">
          Save
        </button>
      </div>
    </div>

    <!-- Normal View -->
    <ng-template #normalView>
      <div class="note-text">
        <!-- Show truncated content if not expanded -->
        <div *ngIf="!isExpanded" class="note-text-container">
          <div class="note-text-content" [innerHTML]="truncatedContent"></div>
        </div>

        <!-- Show full content if expanded -->
        <div *ngIf="isExpanded" class="note-text-container">
          <div class="note-text-content" [innerHTML]="fullContent"></div>
        </div>
      </div>

      <!-- Attachments Display -->
      <div class="note-attachments" [class.compact]="focusMode()" *ngIf="note.attachments?.length > 0">
        <!-- Compact view for focus mode - icons only with tooltips -->
        <div class="attachments-list compact" *ngIf="focusMode()">
          <button 
            type="button"
            class="attachment-icon-btn" 
            *ngFor="let attachment of note.attachments"
            (click)="openAttachment(attachment)"
            [matTooltip]="attachment.name"
            matTooltipPosition="above"
            [class.loading]="loadingAttachment === attachment.cloud_key">
            <mat-icon class="file-icon">{{ getFileIcon(attachment.type) }}</mat-icon>
            <mat-spinner 
              *ngIf="loadingAttachment === attachment.cloud_key" 
              diameter="12">
            </mat-spinner>
          </button>
        </div>
        
        <!-- Full view for normal mode -->
        <div class="attachments-list" *ngIf="!focusMode()">
          <div 
            class="attachment-item clickable" 
            *ngFor="let attachment of note.attachments"
            (click)="openAttachment(attachment)"
            [class.loading]="loadingAttachment === attachment.cloud_key">
            <mat-icon class="file-icon">{{ getFileIcon(attachment.type) }}</mat-icon>
            <span class="attachment-name" [title]="attachment.name">
              {{ attachment.name.length > 25 ? (attachment.name | slice:0:25) + '...' : attachment.name }}
            </span>
            <mat-icon class="download-icon" *ngIf="loadingAttachment !== attachment.cloud_key">
              download
            </mat-icon>
            <mat-spinner 
              *ngIf="loadingAttachment === attachment.cloud_key" 
              diameter="14">
            </mat-spinner>
          </div>
        </div>
      </div>
    </ng-template>
  </div>

  <div class="note-actions" *ngIf="!isEditing" [ngClass]="{'note-actions-focus': focusMode()}">
    <button 
      *ngIf="isTruncated"
      mat-icon-button
      class="action-btn expand-btn" 
      (click)="toggleExpand()">
      <mat-icon>{{ isExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>
    
    <div class="right-actions" *ngIf="!hideActions()">
      <button 
        mat-icon-button
        class="action-btn pin-btn" 
        [ngClass]="{'active': note.pined}"
        (click)="togglePin(note)">
        <mat-icon>{{ note.pined ? 'push_pin' : 'push_pin_outlined' }}</mat-icon>
      </button>
      
      <div class="menu-container" *ngIf="!focusMode()">
        <button mat-icon-button [matMenuTriggerFor]="menu" class="action-btn menu-btn">
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #menu="matMenu">
          <button mat-menu-item (click)="startEdit()">
            <mat-icon>edit</mat-icon>
            <span>Edit</span>
          </button>
          <button mat-menu-item (click)="deleteNote(note)" *ngIf="role == 'ADMIN'">
            <mat-icon>delete</mat-icon>
            <span>Delete</span>
          </button>
          <button mat-menu-item (click)="togglePin(note)">
            <mat-icon>{{ note.pined ? 'push_pin_off' : 'push_pin' }}</mat-icon>
            <span>{{ note.pined ? 'Unpin' : 'Pin' }} this note</span>
          </button>
        </mat-menu>
      </div>
    </div>
  </div>
</div>